'use client';

import {
  BodyDefault,
  Button,
  Field,
  Input,
  SettingsCard,
  SettingsEditDialog,
  toasterApi,
} from '@terra/ui-design-system';
import type {
  CreateMcpIdentityDto,
  McpIdentityConnectionStatusMap,
  McpServerIdentityDto,
  UpsertMcpCredentialDto,
} from '@terra/shared-contracts-mcp-servers';
import {
  isMcpCredentialSubmittable,
  toMcpFormCredentialType,
  type McpFormCredentialType,
} from '@terra/shared-contracts-mcp-servers';
import { isOAuthMcpCredentialType } from '@terra/ui-ai-applications';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Add01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useAuthorizeMcpServerIdentity,
  useCreateMcpServerIdentity,
  useDeleteMcpServerIdentity,
  useMcpServerIdentities,
  useUpdateMcpServerIdentity,
} from '../../../hooks/api/useMcpServerIdentities';
import { useCompleteMcpOAuthCallback } from '../../../hooks/api/useMcpServers';
import { AiApplicationMcpCredentialFields } from './AiApplicationMcpCredentialFields';
import { authorizeMcpOAuthWithElectron } from './electronMcpOAuth';
import { McpServerIdentityRow } from './McpServerIdentityRow';

type McpServerIdentitiesSectionProps = {
  mcpServerId: string;
  discoveryUrl: string;
  isReadOnly?: boolean;
  connectionStatusByIdentityId?: McpIdentityConnectionStatusMap;
  onTestIdentity?: (identityId: string) => void;
  testingIdentityId?: string | null;
  isBulkConnectionTestPending?: boolean;
  openEditIdentityId?: string | null;
  onOpenEditIdentityHandled?: () => void;
};
type IdentityDialogMode =
  | {
      type: 'closed';
    }
  | {
      type: 'create';
    }
  | {
      type: 'edit';
      identity: McpServerIdentityDto;
    };

export const McpServerIdentitiesSection = ({
  mcpServerId,
  discoveryUrl,
  isReadOnly,
  connectionStatusByIdentityId,
  onTestIdentity,
  testingIdentityId,
  isBulkConnectionTestPending,
  openEditIdentityId,
  onOpenEditIdentityHandled,
}: McpServerIdentitiesSectionProps) => {
  const t = useTranslations('aiApplications.mcpIdentities');
  const tAdminMcp = useTranslations('settings.mcpServerForm');
  const tCommon = useTranslations('common');
  const { identities, isLoading, isError, error, refetch } =
    useMcpServerIdentities({
      serverId: mcpServerId,
    });
  const createIdentityMutation = useCreateMcpServerIdentity();
  const updateIdentityMutation = useUpdateMcpServerIdentity();
  const deleteIdentityMutation = useDeleteMcpServerIdentity();
  const authorizeIdentityMutation = useAuthorizeMcpServerIdentity();
  const completeOAuthCallbackMutation = useCompleteMcpOAuthCallback();
  const [dialogMode, setDialogMode] = useState<IdentityDialogMode>({
    type: 'closed',
  });
  const [draftLabel, setDraftLabel] = useState('');
  const [draftScopes, setDraftScopes] = useState('');
  const [draftCredential, setDraftCredential] =
    useState<UpsertMcpCredentialDto | null>(null);
  const [draftCredentialType, setDraftCredentialType] =
    useState<McpFormCredentialType>('OAUTH_DCR');
  const [authorizingIdentityId, setAuthorizingIdentityId] = useState<
    string | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);
  const oauthMessages = useMemo(
    () => ({
      oauthPopupBlocked: tAdminMcp('oauthPopupBlocked'),
      oauthAuthorizationFailed: tAdminMcp('oauthAuthorizationFailed'),
      oauthAuthorizationTimedOut: tAdminMcp('oauthAuthorizationTimedOut'),
      oauthAuthorizationWindowClosed: tAdminMcp(
        'oauthAuthorizationWindowClosed',
      ),
      oauthAuthorizationIncomplete: tAdminMcp('oauthAuthorizationIncomplete'),
    }),
    [tAdminMcp],
  );
  const isEditingIdentity = dialogMode.type === 'edit';
  const canSaveIdentity = useMemo(
    () =>
      Boolean(draftLabel.trim()) &&
      isMcpCredentialSubmittable({
        credentialType: draftCredentialType,
        credential: draftCredential,
        isEditing: isEditingIdentity,
        originalCredentialType:
          dialogMode.type === 'edit'
            ? toMcpFormCredentialType(dialogMode.identity.type)
            : null,
      }),
    [
      dialogMode,
      draftCredential,
      draftCredentialType,
      draftLabel,
      isEditingIdentity,
    ],
  );
  const openCreateDialog = () => {
    setDraftLabel('');
    setDraftScopes('');
    setDraftCredential(null);
    setDraftCredentialType('OAUTH_DCR');
    setDialogMode({ type: 'create' });
  };

  const openEditDialog = useCallback((identity: McpServerIdentityDto) => {
    setDraftLabel(identity.label);
    setDraftScopes((identity.scopes ?? []).join(' '));
    setDraftCredential(null);
    setDraftCredentialType(toMcpFormCredentialType(identity.type));
    setDialogMode({ type: 'edit', identity });
  }, []);

  useEffect(() => {
    if (!openEditIdentityId || isLoading) {
      return;
    }

    const identity = identities.find(
      (entry) => entry.id === openEditIdentityId,
    );

    if (identity) {
      openEditDialog(identity);
    }

    onOpenEditIdentityHandled?.();
  }, [
    identities,
    isLoading,
    onOpenEditIdentityHandled,
    openEditDialog,
    openEditIdentityId,
  ]);
  const closeDialog = () => {
    setDialogMode({ type: 'closed' });
    setDraftLabel('');
    setDraftScopes('');
    setDraftCredential(null);
    setDraftCredentialType('OAUTH_DCR');
  };

  const parseScopes = (value: string): string[] =>
    value
      .split(/[\s,]+/)
      .map((scope) => scope.trim())
      .filter((scope) => scope.length > 0);
  const resolveSubmitCredential = (): UpsertMcpCredentialDto | null => {
    if (draftCredential) {
      return draftCredential;
    }

    if (draftCredentialType === 'NONE') {
      return { type: 'NONE' };
    }

    return null;
  };

  const handleSaveIdentity = async () => {
    if (!canSaveIdentity) {
      return;
    }

    setIsSaving(true);

    try {
      const scopesForNonOAuth = isOAuthMcpCredentialType(draftCredentialType)
        ? undefined
        : parseScopes(draftScopes);

      if (dialogMode.type === 'create') {
        const credential = resolveSubmitCredential();

        if (!credential) {
          return;
        }

        const payload: CreateMcpIdentityDto = {
          label: draftLabel.trim(),
          ...(scopesForNonOAuth !== undefined
            ? { scopes: scopesForNonOAuth }
            : {}),
          ...credential,
        };

        await createIdentityMutation.mutateAsync({
          serverId: mcpServerId,
          data: payload,
        });
      } else if (dialogMode.type === 'edit') {
        const credential = resolveSubmitCredential();
        const credentialTypeChanged =
          draftCredentialType !==
          toMcpFormCredentialType(dialogMode.identity.type);

        await updateIdentityMutation.mutateAsync({
          serverId: mcpServerId,
          identityId: dialogMode.identity.id,
          data: {
            label: draftLabel.trim(),
            ...(scopesForNonOAuth !== undefined
              ? { scopes: scopesForNonOAuth }
              : {}),
            ...(draftCredential || credentialTypeChanged
              ? { credential: credential ?? { type: 'NONE' } }
              : {}),
          },
        });
      }

      toasterApi.success({ title: tCommon('saveChanges') });
      closeDialog();
      await refetch();
    } catch (error: unknown) {
      toasterApi.error({
        title: tCommon('error'),
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteIdentity = async (identity: McpServerIdentityDto) => {
    try {
      await deleteIdentityMutation.mutateAsync({
        serverId: mcpServerId,
        identityId: identity.id,
      });
      toasterApi.success({ title: t('deleteSuccess') });
      await refetch();
    } catch (error: unknown) {
      toasterApi.error({
        title: tCommon('error'),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleAuthorizeIdentity = useCallback(
    async (identity: McpServerIdentityDto) => {
      setAuthorizingIdentityId(identity.id);

      try {
        await authorizeMcpOAuthWithElectron({
          fetchAuthorizeUrl: () =>
            authorizeIdentityMutation.mutateAsync({
              serverId: mcpServerId,
              identityId: identity.id,
            }),
          completeOAuthCallback: async ({ code, state }) => {
            await completeOAuthCallbackMutation.mutateAsync({ code, state });
          },
          messages: oauthMessages,
        });
        await refetch();
      } catch (error: unknown) {
        toasterApi.error({
          title: oauthMessages.oauthAuthorizationFailed,
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        setAuthorizingIdentityId(null);
      }
    },
    [
      authorizeIdentityMutation,
      completeOAuthCallbackMutation,
      mcpServerId,
      oauthMessages,
      refetch,
    ],
  );
  const dialogTitle =
    dialogMode.type === 'create' ? t('addIdentity') : t('editIdentity');

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      {isError ? (
        <BodyDefault color="fg.error">
          {error instanceof Error ? error.message : tCommon('error')}
        </BodyDefault>
      ) : (
        <SettingsCard isLoading={isLoading}>
          {identities.map((identity) => (
            <McpServerIdentityRow
              key={identity.id}
              identity={identity}
              connectionStatus={
                connectionStatusByIdentityId?.[identity.id] ?? null
              }
              isReadOnly={isReadOnly}
              canDelete={identities.length > 1}
              isTestDisabled={
                testingIdentityId != null ||
                Boolean(isBulkConnectionTestPending)
              }
              isAuthorizing={authorizingIdentityId === identity.id}
              onEdit={openEditDialog}
              onTest={onTestIdentity}
              onAuthorize={(target) => {
                void handleAuthorizeIdentity(target);
              }}
              onDelete={(target) => {
                void handleDeleteIdentity(target);
              }}
            />
          ))}
        </SettingsCard>
      )}
      {!isReadOnly && !isError ? (
        <HStack justifyContent="flex-end">
          <Button variant="outline" onClick={openCreateDialog}>
            <Add01Icon size={16} />
            {t('addIdentity')}
          </Button>
        </HStack>
      ) : null}

      <SettingsEditDialog
        open={dialogMode.type !== 'closed'}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        title={dialogTitle}
        cancelText={tCommon('cancel')}
        submitText={tCommon('saveChanges')}
        loading={isSaving}
        isSubmitDisabled={!canSaveIdentity}
        onSubmit={() => {
          void handleSaveIdentity();
        }}
        contentMaxHeight="70vh"
      >
        <VStack alignItems="stretch" gap={4}>
          <Field.Root required>
            <Field.Label>{t('identityLabel')}</Field.Label>
            <Input
              value={draftLabel}
              onChange={(event) => setDraftLabel(event.target.value)}
              placeholder={t('identityLabelPlaceholder')}
            />
          </Field.Root>
          {!isOAuthMcpCredentialType(draftCredentialType) ? (
            <Field.Root>
              <Field.Label>{t('scopesLabel')}</Field.Label>
              <Input
                value={draftScopes}
                onChange={(event) => setDraftScopes(event.target.value)}
                placeholder={t('scopesPlaceholder')}
              />
              <Field.HelperText>{t('scopesHint')}</Field.HelperText>
            </Field.Root>
          ) : null}
          <AiApplicationMcpCredentialFields
            key={
              dialogMode.type === 'edit'
                ? `edit-${dialogMode.identity.id}`
                : 'create-identity'
            }
            mcpServerId={mcpServerId}
            discoveryUrl={discoveryUrl}
            oauthIdentityId={
              dialogMode.type === 'edit' ? dialogMode.identity.id : undefined
            }
            initialAvailableOAuthScopes={
              dialogMode.type === 'edit'
                ? dialogMode.identity.oauthScopesSupported
                : undefined
            }
            initialOauthScopes={
              dialogMode.type === 'edit'
                ? dialogMode.identity.requestedOAuthScopes?.join(' ')
                : undefined
            }
            onCredentialChange={setDraftCredential}
            onCredentialTypeChange={setDraftCredentialType}
            enableOAuthAuthorizeButton={false}
            showNoneConfirmButton={false}
            credentialTypeSeed={
              dialogMode.type === 'edit'
                ? toMcpFormCredentialType(dialogMode.identity.type)
                : null
            }
            credentialIsEditing={isEditingIdentity}
          />
        </VStack>
      </SettingsEditDialog>
    </VStack>
  );
};
